/**
 * Sprint 0149 — Business Card Library.
 *
 * Every scanned card is stored here forever. Repeat visitors are
 * discoverable by mobile number (the primary search). Operator can
 * also click into a card to see the original image + raw OCR text,
 * edit auto-extracted fields, or delete.
 */
import { useState } from 'react'
import {
  Avatar, Button, Card, Descriptions, Drawer, Empty, Input, Modal,
  Pagination, Popconfirm, Space, Tag, Typography, message,
} from 'antd'
import {
  DeleteOutlined, EyeOutlined, IdcardOutlined, SearchOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { businessCardsApi, type BusinessCard } from '@/features/gate/api'

const { Text, Paragraph } = Typography

export default function BusinessCardLibraryPage() {
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [viewing, setViewing] = useState<BusinessCard | null>(null)
  const canEdit = usePermission('visitor.update')

  const { data, isLoading } = useQuery({
    queryKey: ['business-cards', page, search],
    queryFn: () => businessCardsApi.list({
      page, size: 20, search: search || undefined,
    }),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => businessCardsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['business-cards'] })
      message.success('Card removed')
      setViewing(null)
    },
    onError: () => message.error('Delete failed'),
  })

  return (
    <div>
      <PageHeader
        title="Business Card Library"
        subtitle="Every visitor business card scanned at the gate, searchable by mobile, company, or name."
      />

      <Card size="small" style={{ marginBottom: 12 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search by mobile, company, name or email…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          style={{ maxWidth: 420 }}
        />
      </Card>

      <Card size="small" loading={isLoading}>
        {data?.items?.length ? (
          <>
            <Space wrap size={12}>
              {data.items.map((c) => (
                <Card
                  key={c.id}
                  size="small"
                  hoverable
                  style={{ width: 240 }}
                  cover={
                    c.image_url ? (
                      <img
                        src={c.image_url}
                        alt={c.extracted_name || 'Business card'}
                        style={{ height: 130, objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        height: 130, background: '#fafafa',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Avatar
                          icon={<IdcardOutlined />} size={48} shape="square"
                          style={{ background: '#bfbfbf' }}
                        />
                      </div>
                    )
                  }
                  onClick={() => setViewing(c)}
                  actions={[
                    <EyeOutlined key="view" />,
                  ]}
                >
                  <Card.Meta
                    title={c.extracted_name || <Text type="secondary">No name</Text>}
                    description={
                      <Space direction="vertical" size={0} style={{ width: '100%' }}>
                        {c.extracted_company && (
                          <Text ellipsis style={{ fontSize: 12 }}>{c.extracted_company}</Text>
                        )}
                        {c.extracted_mobile && (
                          <Text strong style={{ fontSize: 12 }}>
                            +91 {c.extracted_mobile}
                          </Text>
                        )}
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {dayjs(c.created_at).fromNow()}
                        </Text>
                      </Space>
                    }
                  />
                </Card>
              ))}
            </Space>
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Pagination
                current={page}
                pageSize={20}
                total={data.total}
                onChange={setPage}
                showSizeChanger={false}
              />
            </div>
          </>
        ) : (
          <Empty description="No business cards scanned yet" />
        )}
      </Card>

      {/* Detail drawer */}
      <Drawer
        title={viewing ? `Card — ${viewing.extracted_name || 'unknown'}` : ''}
        width={520}
        open={!!viewing}
        onClose={() => setViewing(null)}
        extra={
          viewing && canEdit ? (
            <Popconfirm
              title="Remove this card from library?"
              okText="Remove"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteMut.mutate(viewing.id)}
            >
              <Button danger icon={<DeleteOutlined />}
                loading={deleteMut.isPending}>
                Delete
              </Button>
            </Popconfirm>
          ) : null
        }
      >
        {viewing && (
          <>
            {viewing.image_url && (
              <img
                src={viewing.image_url}
                alt="Business card"
                style={{
                  width: '100%', borderRadius: 8, marginBottom: 12,
                  background: '#000',
                }}
              />
            )}
            <Descriptions
              size="small" column={1} colon bordered
              labelStyle={{ width: 130 }}
            >
              <Descriptions.Item label="Name">
                {viewing.extracted_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Company">
                {viewing.extracted_company || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Designation">
                {viewing.extracted_designation || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Mobile">
                {viewing.extracted_mobile
                  ? <Text copyable>{viewing.extracted_mobile}</Text>
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {viewing.extracted_email
                  ? <Text copyable>{viewing.extracted_email}</Text>
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Website">
                {viewing.extracted_website || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                {viewing.extracted_address || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="OCR engine">
                {viewing.ocr_engine || '—'}
                {viewing.ocr_confidence != null && (
                  <Tag color="blue" style={{ marginLeft: 6 }}>
                    {Number(viewing.ocr_confidence).toFixed(0)}% conf
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Captured">
                {dayjs(viewing.created_at).format('DD-MMM-YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Linked to visitor">
                {viewing.visitor_log_id
                  ? <Tag color="green">Linked</Tag>
                  : <Tag>Library only</Tag>}
              </Descriptions.Item>
            </Descriptions>

            {viewing.raw_ocr_text && (
              <Card
                size="small"
                title="Raw OCR text"
                style={{ marginTop: 12 }}
              >
                <Paragraph
                  copyable
                  style={{
                    whiteSpace: 'pre-wrap', fontSize: 11, color: '#555',
                    margin: 0,
                  }}
                >
                  {viewing.raw_ocr_text}
                </Paragraph>
              </Card>
            )}
          </>
        )}
      </Drawer>
    </div>
  )
}
